import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";

import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { formatDateTimeCompact } from "@/utils/relative-time";

import { type SchoolBid } from "../../../BidRequests/types";
import { formatAmount, formatMoney } from "../../../BidRequests/utils";

interface ViewBidDialogProps {
  bid: SchoolBid | null;
  canAccept: boolean;
  onClose: () => void;
  onAccept: (bid: SchoolBid) => void;
}

export const ViewBidDialog = ({ bid, canAccept, onClose, onAccept }: ViewBidDialogProps) => {
  const { t } = useTranslation();

  if (!bid) {
    return null;
  }

  const currency = bid.currency;
  const hasTransport = Number(bid.transport_cost) > 0;
  const updatedOn = formatDateTimeCompact(bid.updated_at ?? bid.submitted_at);

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t("bidRequests.detail.viewBidDialog.title", { aggregator: bid.aggregator_name })}
          </DialogTitle>
          {updatedOn && (
            <DialogDescription>
              {t("bidRequests.detail.viewBidDialog.updatedOn", { date: updatedOn })}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("bidRequests.detail.viewBidDialog.commodity")}</TableHead>
                <TableHead>{t("bidRequests.detail.viewBidDialog.quantity")}</TableHead>
                <TableHead>{t("bidRequests.detail.viewBidDialog.unitPrice")}</TableHead>
                <TableHead className="text-right">
                  {t("bidRequests.detail.viewBidDialog.totalPrice")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bid.items.map(item => (
                <TableRow key={item.id}>
                  <TableCell>{item.commodity_name}</TableCell>
                  <TableCell>
                    {formatAmount(item.quantity)} {item.unit_of_measure_code}
                  </TableCell>
                  <TableCell>
                    {formatMoney(currency, item.unit_price)} / {item.unit_of_measure_code}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatMoney(currency, item.line_total)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-col items-end gap-1 text-sm">
          {hasTransport && (
            <p>
              <span className="text-muted-foreground">
                {t("bidRequests.detail.viewBidDialog.transportation")}{" "}
              </span>
              <span className="font-medium">{formatMoney(currency, bid.transport_cost)}</span>
            </p>
          )}
          <p className="font-semibold text-lg">
            {t("bidRequests.detail.viewBidDialog.total")} {formatMoney(currency, bid.total)}
          </p>
        </div>

        <DialogFooter className="sm:justify-between">
          <WFPButton type="button" variant="outline" onClick={onClose}>
            <ArrowLeft className="size-4" />
            {t("bidRequests.detail.viewBidDialog.back")}
          </WFPButton>
          {canAccept && (
            <WFPButton type="button" onClick={() => onAccept(bid)}>
              {t("bidRequests.detail.viewBidDialog.accept")}
            </WFPButton>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
